import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { ExpandableFieldItem } from './ExpandableField';
import { EXPANDABLE_ITEM_HEIGHT } from '../constants';
import { formatActionTimestamp } from '@features/appointments/utils/scheduledActionUtils';

interface SchedulableItemProps {
  item: ExpandableFieldItem;
  onEdit: (item: ExpandableFieldItem) => void;
  onDelete: (itemId: string | number) => void;
  isFormType?: boolean;
}

const SchedulableItem: React.FC<SchedulableItemProps> = ({
  item,
  onEdit,
  onDelete,
  isFormType = false,
}) => {
  const theme = useTheme();

  // Helper function to check if item is editable
  const isEditable = (status?: string): boolean => {
    return status === 'scheduled' || status === 'pending' || status === 'configured' || !status;
  };

  // Helper function to get tooltip content based on status
  const getTooltipContent = (item: ExpandableFieldItem): string => {
    if (item.status === 'failed' && item.errorMessage) {
      return item.errorMessage;
    }
    if (item.status === 'sent' && item.executedAt) {
      return `Sent at ${formatActionTimestamp(item.executedAt)}`;
    }
    return '';
  };

  // Helper function to determine if tooltip should be shown
  const shouldShowTooltip = (status?: string): boolean => {
    return status === 'failed' || status === 'sent';
  };

  // Helper function to get chip label based on status
  const getChipLabel = (item: ExpandableFieldItem): string => {
    switch (item.status) {
      case 'scheduled':
        return `scheduled for ${item.scheduledFor || formatActionTimestamp(item.scheduledAt)}`;
      case 'sending':
        return 'sending now';
      case 'sent':
        return 'sent';
      case 'failed':
        return 'failed';
      case 'cancelled':
        return 'canceled';
      default:
        return item.timing || '';
    }
  };

  const getStatusIcon = (status?: string) => {
    const iconProps = { fontSize: 'small' as const };
    switch (status) {
      case 'scheduled':
        return <EventAvailableIcon {...iconProps} color="info" />;
      case 'sending':
        return <ScheduleIcon {...iconProps} color="warning" />;
      case 'sent':
        return <CheckCircleIcon {...iconProps} color="success" />;
      case 'failed':
        return <ErrorIcon {...iconProps} color="error" />;
      case 'cancelled':
        return <CancelIcon {...iconProps} sx={{ color: 'text.secondary' }} />;
      default:
        return <AccessTimeIcon {...iconProps} color="disabled" />;
    }
  };

  const getStatusColor = (
    status?: string,
  ): 'info' | 'warning' | 'primary' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'scheduled':
        return 'info';
      case 'sending':
        return 'warning';
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        height: EXPANDABLE_ITEM_HEIGHT,
        '&:hover .action-button': {
          opacity: isEditable(item.status) ? 1 : 0,
        },
        '&:hover': {
          backgroundColor: isEditable(item.status)
            ? alpha(theme.palette.action.hover, 0.04)
            : 'transparent',
          boxShadow: isEditable(item.status) ? theme.shadows[2] : 'none',
        },
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        cursor: isEditable(item.status) ? 'pointer' : 'default',
      }}
      onClick={() => isEditable(item.status) && onEdit(item)}
    >
      <CardContent
        sx={{
          py: 0,
          px: 2,
          width: '100%',
          '&:last-child': { pb: 0 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
            {getStatusIcon(item.status)}
            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
              {item.name}
            </Typography>
            <Tooltip
              title={getTooltipContent(item)}
              disableHoverListener={!shouldShowTooltip(item.status)}
            >
              <Chip
                label={getChipLabel(item)}
                size="small"
                color={getStatusColor(item.status)}
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {isEditable(item.status) && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                className="action-button"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onEdit(item);
                }}
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                className="action-button"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.04),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SchedulableItem;
